// src/components/sales/pages/marketing-dashboard/MarketingDashboardView.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getStatistics,
  getClientGrowth,
  getCampaignPerformance,
  getTopClients,
  getRecentActivity,
} from "../../../../services/marketingDashboard.service";

const glassCard =
  "rounded-2xl border border-white/15 bg-white/10 backdrop-blur-2xl shadow-[0_0_28px_rgba(255,0,255,0.10)]";

function monthLabel(y, m) {
  const d = new Date(y, (m || 1) - 1, 1);
  return d.toLocaleString(undefined, { month: "short", year: "numeric" });
}

function ClientGrowthCard({ data = [], months = 6 }) {
  const { t, i18n } = useTranslation();
  void i18n;
  const normalized = useMemo(() => {
    const arr = Array.isArray(data) ? data : [];
    // نفس منطقك: map + sort ascending
    return arr
      .map((x) => ({ year: x.year, month: x.month, count: x.count ?? 0 }))
      .sort((a, b) => (a.year - b.year) || (a.month - b.month));
  }, [data]);

  if (!normalized.length) {
    return (
      <div className={glassCard + " p-5"}>
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-white">
              {t("sales.marketingDashboard.clientGrowth.title")}
            </div>
            <div className="text-xs text-white/70 mt-1">
              {t("sales.marketingDashboard.clientGrowth.lastMonths", { count: months })}
            </div>
          </div>
          <div className="px-3 py-1 rounded-xl bg-white/10 border border-white/15 text-white text-xs">
            {t("sales.marketingDashboard.clientGrowth.noData")}
          </div>
        </div>

        <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-4">
          <div className="text-xs text-white/70">
            {t("sales.marketingDashboard.clientGrowth.noDataHint")}
          </div>
        </div>
      </div>
    );
  }

  // نفس منطقك: لو نقطة واحدة
  if (normalized.length === 1) {
    const p = normalized[0];
    return (
      <div className={glassCard + " p-5"}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-white">
              {t("sales.marketingDashboard.clientGrowth.title")}
            </div>
            <div className="text-xs text-white/70 mt-1">{monthLabel(p.year, p.month)}</div>
          </div>

          <div className="px-3 py-1 rounded-xl bg-white/10 border border-white/15 text-white text-xs">
            {t("sales.marketingDashboard.clientGrowth.lastMonths", { count: months })}
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <div className="text-[11px] text-white/70">
              {t("sales.marketingDashboard.clientGrowth.singleMonthNew")}
            </div>
            <div className="mt-2 text-4xl font-bold text-white">{p.count}</div>
            <div className="mt-2 text-xs text-white/60">
              {t("sales.marketingDashboard.clientGrowth.singleMonthTip")}
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-pink-500/15 to-purple-600/15 p-4">
            <div className="text-[11px] text-white/70">
              {t("sales.marketingDashboard.clientGrowth.preview")}
            </div>
            <div className="mt-4 h-28 rounded-2xl border border-white/10 bg-white/5 flex items-center justify-center">
              <div className="text-xs text-white/80 font-semibold">
                {t("sales.marketingDashboard.clientGrowth.chartLabel")}
              </div>
            </div>
            <div className="mt-3 text-[11px] text-white/60">
              {t("sales.marketingDashboard.clientGrowth.unlockFullChart")}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // نفس منطقك: max + bar height
  const max = Math.max(...normalized.map((x) => x.count || 0), 1);

  return (
    <div className={glassCard + " p-5"}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-white">
            {t("sales.marketingDashboard.clientGrowth.title")}
          </div>
          <div className="text-xs text-white/70 mt-1">
            {t("sales.marketingDashboard.clientGrowth.newPerMonth")}
          </div>
        </div>

        <div className="px-3 py-1 rounded-xl bg-white/10 border border-white/15 text-white text-xs">
          {t("sales.marketingDashboard.clientGrowth.lastMonths", { count: months })}
        </div>
      </div>

      {/* Chart */}
      <div className="mt-5 rounded-2xl border border-white/10 bg-white/5 p-4">
        <div className="flex items-end gap-3 h-44">
          {normalized.map((p, idx) => {
            const h = Math.round(((p.count || 0) / max) * 100);

            return (
              <div key={`${p.year}-${p.month}-${idx}`} className="flex-1 flex flex-col items-center gap-2 group">
                {/* Bar frame */}
                <div className="relative w-full h-32 rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
                  {/* grid vibe */}
                  <div className="absolute inset-0 opacity-40">
                    <div className="h-full w-full bg-[linear-gradient(to_top,rgba(255,255,255,0.06)_1px,transparent_1px)] bg-[length:100%_22px]" />
                  </div>

                  {/* bar */}
                  <div
                    className="absolute bottom-0 left-0 right-0 rounded-2xl bg-gradient-to-t from-pink-500/60 to-purple-600/60 transition-all duration-200 group-hover:from-pink-500/80 group-hover:to-purple-600/80"
                    style={{ height: `${Math.max(h, 4)}%` }}
                    title={`${monthLabel(p.year, p.month)}: ${p.count}`}
                  />

                  {/* hover label */}
                  <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="px-2 py-1 rounded-lg bg-black/40 border border-white/10 text-[11px] text-white">
                      {p.count}
                    </div>
                  </div>
                </div>

                {/* x label */}
                <div className="text-[11px] text-white/70 text-center leading-tight">
                  {new Date(p.year, p.month - 1, 1).toLocaleString(undefined, { month: "short" })}
                </div>

                {/* value */}
                <div className="text-[11px] text-white font-semibold">{p.count}</div>
              </div>
            );
          })}
        </div>

        {/* Footer note */}
        <div className="mt-3 flex items-center justify-between text-[11px] text-white/60">
          <div>{t("sales.marketingDashboard.clientGrowth.points", { count: normalized.length })}</div>
          <div>{t("sales.marketingDashboard.clientGrowth.max", { count: max })}</div>
        </div>
      </div>
    </div>
  );
}


export default function MarketingDashboardView() {
  const { t, i18n } = useTranslation();
  void i18n;
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [growth, setGrowth] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [topClients, setTopClients] = useState([]);
  const [activity, setActivity] = useState([]);
  const months = 6;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, g, p, t, a] = await Promise.all([
        getStatistics(),
        getClientGrowth(months),
        getCampaignPerformance(10),
        getTopClients(10),
        getRecentActivity(7),
      ]);

      // backend may return plain objects/arrays
      setStats(s?.data ?? s);
      setGrowth(g?.data ?? g ?? []);
      setPerformance(p?.data ?? p ?? []);
      setTopClients(t?.data ?? t ?? []);
      setActivity(a?.data ?? a ?? []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const statCards = useMemo(() => {
    const s = stats || {};
    return [
      { k: t("sales.marketingDashboard.stats.totalClients"), v: s.totalClients ?? 0 },
      {
        k: t("sales.marketingDashboard.stats.totalSentMessages"),
        v: s.totalSentMessages ?? 0,
      },
      { k: t("sales.marketingDashboard.stats.totalInteractions"), v: s.totalInteractions ?? 0 },
      { k: t("sales.marketingDashboard.stats.activeCampaigns"), v: s.activeCampaigns ?? 0 },
      { k: t("sales.marketingDashboard.stats.leads"), v: s.leadsCount ?? 0 },
      { k: t("sales.marketingDashboard.stats.prospects"), v: s.prospectsCount ?? 0 },
      { k: t("sales.marketingDashboard.stats.customers"), v: s.customersCount ?? 0 },
      { k: t("sales.marketingDashboard.stats.totalCampaigns"), v: s.totalCampaigns ?? 0 },
    ];
  }, [stats, t]);

  return (
    <div className="w-full">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-lg font-bold text-white">
            {t("sales.marketingDashboard.title")}
          </div>
          <div className="text-xs text-white/70">
            {t("sales.marketingDashboard.subtitle")}
          </div>
        </div>

        <button
          onClick={load}
          disabled={loading}
          className="px-4 py-2 rounded-xl bg-white/10 border border-white/15 text-white hover:bg-white/15 disabled:opacity-60"
        >
          {loading ? t("common.loading") : t("common.refresh")}
        </button>
      </div>

      {/* Statistics */}
      <div className={glassCard + " p-4"}>
        <div className="text-sm font-semibold text-white">
          {t("sales.marketingDashboard.statisticsTitle")}
        </div>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
          {statCards.map((c) => (
            <div key={c.k} className="rounded-xl border border-white/10 bg-white/5 p-3">
              <div className="text-[11px] text-white/70">{c.k}</div>
              <div className="mt-1 text-xl font-bold text-white">{c.v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Middle grid */}
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ClientGrowthCard data={growth} months={months} />

        <div className={glassCard + " p-4"}>
          <div className="text-sm font-semibold text-white">
            {t("sales.marketingDashboard.campaignPerformanceTitle")}
          </div>

          <div className="mt-3">
            {Array.isArray(performance) && performance.length ? (
              <div className="space-y-3">
                {performance.map((x, idx) => (
                  <div key={idx} className="rounded-xl border border-white/10 bg-white/5 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-semibold text-white truncate">
                        {x.campaignName ||
                          t("sales.marketingDashboard.campaignFallback", { count: idx + 1 })}
                      </div>
                      <div className="text-xs text-white/70">
                        {t("sales.marketingDashboard.scoreLabel")}{" "}
                        <span className="text-white font-semibold">
                          {x.score ?? x.performanceScore ?? 0}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-white/70">
                {t("sales.marketingDashboard.noPerformance")}
                <div className="mt-1 text-[11px] text-white/60">
                  {t("sales.marketingDashboard.noPerformanceHint")}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={glassCard + " p-4"}>
          <div className="text-sm font-semibold text-white">
            {t("sales.marketingDashboard.topClientsTitle")}
          </div>

          <div className="mt-3 overflow-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-white/70">
                <tr className="border-b border-white/10">
                  <th className="text-left py-2 pr-3">
                    {t("sales.marketingDashboard.topClients.table.company")}
                  </th>
                  <th className="text-left py-2 pr-3">
                    {t("sales.marketingDashboard.topClients.table.client")}
                  </th>
                  <th className="text-left py-2 pr-3">
                    {t("sales.marketingDashboard.topClients.table.status")}
                  </th>
                  <th className="text-right py-2 pr-3">
                    {t("sales.marketingDashboard.topClients.table.interactions")}
                  </th>
                  <th className="text-right py-2">
                    {t("sales.marketingDashboard.topClients.table.notes")}
                  </th>
                </tr>
              </thead>
              <tbody className="text-white">
                {(topClients || []).map((c, idx) => (
                  <tr key={idx} className="border-b border-white/5">
                    <td className="py-2 pr-3">{c.companyName || "-"}</td>
                    <td className="py-2 pr-3">{c.clientName || "-"}</td>
                    <td className="py-2 pr-3">
                      <span className="px-2 py-1 rounded-lg bg-white/10 border border-white/10 text-xs">
                        {c.clientStatus || "-"}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-right">{c.totalInteractions ?? 0}</td>
                    <td className="py-2 text-right">{c.totalNotes ?? 0}</td>
                  </tr>
                ))}
                {!topClients?.length && (
                  <tr>
                    <td colSpan={5} className="py-3 text-xs text-white/60">
                      {t("sales.marketingDashboard.topClients.empty")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className={glassCard + " p-4 lg:col-span-2"}>
          <div className="text-sm font-semibold text-white">
            {t("sales.marketingDashboard.recentActivityTitle")}
          </div>

          <div className="mt-3 space-y-3">
            {(activity || []).map((a, idx) => (
              <div key={idx} className="rounded-xl border border-white/10 bg-white/5 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-white">
                      {a.type || t("sales.marketingDashboard.activityFallback")}{" "}
                      <span className="text-white/60 text-xs">— {a.clientName || "-"}</span>
                    </div>
                    <div className="text-xs text-white/70 mt-1">{a.description || "-"}</div>
                    {a.details && (
                      <div className="mt-2 text-xs text-white/80 bg-black/20 border border-white/10 rounded-lg p-2">
                        {a.details}
                      </div>
                    )}
                  </div>

                  <div className="text-[11px] text-white/60 whitespace-nowrap">
                    {a.date ? new Date(a.date).toLocaleString() : ""}
                  </div>
                </div>
              </div>
            ))}
            {!activity?.length && (
              <div className="text-xs text-white/60">
                {t("sales.marketingDashboard.recentActivityEmpty")}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
